import { ReadSpacesListDTO } from '@/modules/spaces/hooks/types'
import { Button2, IonIcon } from '@valuecase/ui-components'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@valuecase/ui-components/src/components/ui/dialog'
import { useCallback, useMemo } from 'react'
import GradientSpacesList from './GradientSpacesList'
interface DeleteSpacesDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  selectedSpaces: ReadSpacesListDTO[]
  onDelete: () => void
  isDeleting?: boolean
  isArchiving?: boolean
  onArchive: () => void
}

const DeleteSpacesDialog = ({
  isOpen,
  onOpenChange,
  selectedSpaces,
  onDelete,
  onArchive,
  isDeleting,
  isArchiving,
}: DeleteSpacesDialogProps) => {
  const handleDeleteConfirm = useCallback(async () => {
    onDelete()
  }, [onDelete])

  const areArchivedSpaces = useMemo(
    () => selectedSpaces.some((space) => space.archived),
    [selectedSpaces],
  )

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className='min-w-[580px]'>
        <DialogHeader className='flex items-center gap-2 bg-white border-none'>
          <IonIcon name='trash' className='w-10 h-10 text-warning-s4' />
          <DialogTitle>
            Delete {selectedSpaces.length === 1 ? 'Space' : `${selectedSpaces.length} Spaces`}
          </DialogTitle>
          <DialogDescription>
            {selectedSpaces.length === 1
              ? `You're about to delete the space ${selectedSpaces[0].companyName}. This is non-reversible. Do you want to proceed?`
              : `You're about to delete these spaces. This is non-reversible. Do you want to proceed?`}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className='bg-white border-none flex flex-col gap-4'>
          <GradientSpacesList spaces={selectedSpaces} />
          {!areArchivedSpaces ? (
            <span className='text-sm  text-grey-s5 text-center'>
              Or keep the data, just hide it by <span className='font-bold'>archiving</span> the
              spaces instead.
            </span>
          ) : (
            <></>
          )}
        </DialogBody>
        <DialogFooter>
          {areArchivedSpaces ? (
            <div className='flex justify-between w-full gap-3'>
              <Button2 variant='outlined' className='w-full' onClick={() => onOpenChange(false)}>
                Cancel
              </Button2>
              <Button2
                leadingIcon='trash'
                variant='danger'
                className='w-full'
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button2>
            </div>
          ) : (
            <div className='flex justify-between w-full'>
              <Button2
                className='flex-start'
                variant='ghost'
                onClick={() => onOpenChange(false)}
                disabled={isDeleting || isArchiving}
              >
                Cancel
              </Button2>
              <div className='flex gap-2 w-fit'>
                <Button2
                  leadingIcon='file-tray'
                  variant='outlined'
                  onClick={() => onArchive()}
                  disabled={isDeleting || isArchiving}
                >
                  {isArchiving ? 'Archiving...' : 'Archive'}
                </Button2>
                <Button2 leadingIcon='trash' variant='danger' onClick={handleDeleteConfirm}>
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button2>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DeleteSpacesDialog
